import { registerDestructor } from '@ember/destroyable';
import { isTesting, macroCondition } from '@embroider/macros';
import Modifier, { type ArgsFor } from 'ember-modifier';
import { addZoomControls } from 'frontend-utils/utils/google-maps/zoom-control';
import {
  getGoogleMapsStyles,
  GOOGLE_MAPS_SHARED_CONFIG,
} from 'lumen/utils/constants/google-maps-constants';
import { isGoogleMapsAvailable } from 'lumen/utils/google-maps';

export interface InitGoogleMapSignature {
  Args: {
    Positional: [handler: (map: google.maps.Map) => void, isLoading?: boolean];
  };
  Element: HTMLElement;
}

export default class InitGoogleMapModifier extends Modifier<InitGoogleMapSignature> {
  cleanupZoomControls: () => void = (): void => {};
  map: google.maps.Map | null = null;

  constructor(
    owner: ConstructorParameters<typeof Modifier>[0],
    args: ArgsFor<InitGoogleMapSignature>,
  ) {
    super(owner, args);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    registerDestructor(this, this.cleanup);
  }

  cleanup(instance: InitGoogleMapModifier): void {
    instance.map = null;
    instance.cleanupZoomControls();
  }

  initMap(element: HTMLElement): void {
    const googleMapsConfig = {
      ...GOOGLE_MAPS_SHARED_CONFIG,
    };

    this.map = new google.maps.Map(element, {
      ...googleMapsConfig,
      styles: getGoogleMapsStyles().BLACK_AND_WHITE,
    });

    this.cleanupZoomControls = addZoomControls(this.map);
  }

  modify(
    element: InitGoogleMapSignature['Element'],
    [handler, isLoading]: InitGoogleMapSignature['Args']['Positional'],
  ): void {
    // Disable maps rendering for visual tests
    if (macroCondition(isTesting())) {
      return;
    }

    // Maps can be blocked (e.g. in China) or still loading; skip init so the
    // consuming component degrades to no map instead of throwing.
    if (!isGoogleMapsAvailable()) {
      return;
    }

    if (!this.map) {
      this.initMap(element);
    }

    if (!isLoading && this.map) {
      handler(this.map);
    }
  }
}
